"use client"

import { useState, useCallback } from "react"
import { View, StyleSheet } from "react-native"
import { Modal, Text, Button, Portal, useTheme, TextInput, Surface } from "react-native-paper"
import convert from "convert-units"
import { useUnits } from "../contexts/UnitsContext"
import type { ActiveUnits } from "../types/table"

interface UnitOption {
  label: string
  value: string
}

// Filter for construction/engineering relevant units
const relevantUnits = {
  length: ["mm", "cm", "m", "km", "in", "ft", "yd"],
  mass: ["g", "kg", "t", "lb"],
  volume: ["mm3", "cm3", "m3"],
  area: ["mm2", "cm2", "m2", "ft2", "yd2"],
  temperature: ["C", "F", "K"],
  power: ["W", "kW", "MW"],
  voltage: ["mV", "V", "kV"],
}

export function UnitsOfMeasure({ visible, onDismiss }: { visible: boolean; onDismiss: () => void }) {
  const { activeUnits, setActiveUnits } = useUnits()
  const [selectedUnits, setSelectedUnits] = useState<ActiveUnits>(activeUnits)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const theme = useTheme()

  const getMeasureUnits = useCallback((measure: string): UnitOption[] => {
    const units = relevantUnits[measure as keyof typeof relevantUnits] || []
    return units.map((unit) => {
      let label = unit
      // Add special formatting for temperature units
      if (measure === "temperature") {
        switch (unit) {
          case "C":
            label = "°C (Celsius)"
            break
          case "F":
            label = "°F (Fahrenheit)"
            break
          case "K":
            label = "K (Kelvin)"
            break
        }
      }
      // Add special formatting for power units
      else if (measure === "power") {
        switch (unit) {
          case "W":
            label = "W (Watts)"
            break
          case "kW":
            label = "kW (Kilowatts)"
            break
          case "MW":
            label = "MW (Megawatts)"
            break
        }
      }
      // Add special formatting for voltage units
      else if (measure === "voltage") {
        switch (unit) {
          case "mV":
            label = "mV (Millivolts)"
            break
          case "V":
            label = "V (Volts)"
            break
          case "kV":
            label = "kV (Kilovolts)"
            break
        }
      }
      // For other units, use convert-units descriptions
      else {
        try {
          label = `${unit} (${convert().describe(unit).plural})`
        } catch (error) {
          // Could not get description for unit
        }
      }
      return {
        label,
        value: unit,
      }
    })
  }, [])

  const measures = [
    { key: "length", label: "Length" },
    { key: "mass", label: "Mass" },
    { key: "volume", label: "Volume" },
    { key: "area", label: "Area" },
    { key: "temperature", label: "Temperature" },
    { key: "power", label: "Power" },
    { key: "voltage", label: "Voltage" },
  ]

  const handleUnitChange = (measure: keyof ActiveUnits, value: string) => {
    setSelectedUnits((prev) => ({
      ...prev,
      [measure]: value,
    }))
    setOpenDropdown(null)
  }

  const handleSave = () => {
    setActiveUnits(selectedUnits)
    onDismiss()
  }

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={[styles.container, { backgroundColor: theme.colors.elevation.level2 }]}
      >
        <Text variant="headlineSmall" style={[styles.title, { color: theme.colors.onSurface }]}>
          Units of Measure
        </Text>

        <View style={styles.modalContent}>
          <View style={styles.grid}>
            {measures.map(({ key, label }, index) => (
              <View key={key} style={[styles.row, { zIndex: 1000 - index }]}>
                <Text style={[styles.measureLabel, { color: theme.colors.onSurfaceVariant }]}>{label}</Text>
                <View style={styles.selectContainer}>
                  <TextInput
                    value={selectedUnits[key as keyof ActiveUnits]}
                    mode="outlined"
                    right={
                      <TextInput.Icon
                        icon="menu-down"
                        onPress={() => setOpenDropdown(openDropdown === key ? null : key)}
                      />
                    }
                    style={[styles.input, { backgroundColor: theme.colors.elevation.level1 }]}
                    contentStyle={{ height: 36 }}
                    dense
                    editable={false}
                    onPressIn={() => setOpenDropdown(key)}
                  />
                  {openDropdown === key && (
                    <Surface
                      style={[
                        styles.dropdown,
                        {
                          backgroundColor: theme.colors.elevation.level3,
                          position: "absolute",
                          left: 0,
                          right: 0,
                          top: "100%",
                          marginTop: 4,
                          zIndex: 2000,
                        },
                      ]}
                      elevation={8}
                    >
                      {getMeasureUnits(key).map((unit) => (
                        <Button
                          key={unit.value}
                          onPress={() => handleUnitChange(key as keyof ActiveUnits, unit.value)}
                          style={[
                            styles.dropdownItem,
                            selectedUnits[key as keyof ActiveUnits] === unit.value && {
                              backgroundColor: theme.colors.primaryContainer,
                            },
                          ]}
                          labelStyle={[
                            styles.dropdownItemLabel,
                            { color: theme.colors.onSurface },
                            selectedUnits[key as keyof ActiveUnits] === unit.value && {
                              color: theme.colors.onPrimaryContainer,
                            },
                          ]}
                        >
                          {unit.label}
                        </Button>
                      ))}
                    </Surface>
                  )}
                </View>
              </View>
            ))}
          </View>

          <View style={[styles.actions, { zIndex: 1 }]}>
            <Button onPress={onDismiss} textColor={theme.colors.primary}>
              Cancel
            </Button>
            <Button mode="contained" onPress={handleSave} style={{ borderRadius: 4 }}>
              Save
            </Button>
          </View>
        </View>
      </Modal>
    </Portal>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: 24,
    margin: 20,
    borderRadius: 8,
    maxWidth: 400,
    alignSelf: "center",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  modalContent: {
    position: "relative",
    zIndex: 1,
  },
  title: {
    marginBottom: 20,
    fontWeight: "500",
  },
  grid: {
    gap: 12,
    marginBottom: 20,
    position: "relative",
    zIndex: 1000,
  },
  row: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 8,
    position: "relative",
  },
  measureLabel: {
    fontSize: 14,
    width: 70,
    paddingTop: 8,
  },
  selectContainer: {
    flex: 1,
    maxWidth: 220,
    position: "relative",
  },
  input: {
    height: 36,
  },
  dropdown: {
    borderRadius: 4,
    overflow: "hidden",
    maxHeight: 200,
    width: "100%",
  },
  dropdownItem: {
    borderRadius: 0,
    height: 36,
    justifyContent: "center",
  },
  dropdownItemLabel: {
    fontSize: 14,
  },
  actions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    position: "relative",
  },
})

